import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Gift, Settings, Users, Plus, Pencil, Trash2, Star, 
  TrendingUp, Calendar, Award, Phone, Cake, Search,
  ChevronRight, Eye, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoyalty() {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState({
    points_per_checkin: 10,
    points_per_referral: 50,
    referral_bonus_for_new_user: 25
  });
  const [rewards, setRewards] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
  
  // Reward Dialog
  const [isRewardDialogOpen, setIsRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewardForm, setRewardForm] = useState({
    name: '',
    description: '',
    points_required: '',
    image_url: '',
    stock: '0',
    start_date: '',
    end_date: '',
    birthday_only: false,
    active: true
  });

  // User Detail Dialog
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch users when branch filter changes
  useEffect(() => {
    if (!loading) {
      fetchUsers();
    }
  }, [selectedBranchFilter]);

  const fetchData = async () => {
    try {
      const [configRes, rewardsRes, usersRes, statsRes, branchesRes] = await Promise.all([
        axios.get(`${API}/loyalty/config`, getAuthHeaders()),
        axios.get(`${API}/loyalty/rewards`, getAuthHeaders()),
        axios.get(`${API}/loyalty/users`, getAuthHeaders()),
        axios.get(`${API}/loyalty/stats`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders()),
      ]);
      setConfig(configRes.data);
      setRewards(rewardsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = selectedBranchFilter !== 'all' ? `?branch_id=${selectedBranchFilter}` : '';
      const response = await axios.get(`${API}/loyalty/users${params}`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/loyalty/config`, config, getAuthHeaders());
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleRewardSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...rewardForm,
        points_required: parseInt(rewardForm.points_required),
        stock: parseInt(rewardForm.stock) || 0,
        start_date: rewardForm.start_date || null,
        end_date: rewardForm.end_date || null,
        birthday_only: !!rewardForm.birthday_only,
      };

      if (editingReward) {
        await axios.put(`${API}/loyalty/rewards/${editingReward.id}`, data, getAuthHeaders());
        toast.success('Recompensa actualizada');
      } else {
        await axios.post(`${API}/loyalty/rewards`, data, getAuthHeaders());
        toast.success('Recompensa creada');
      }
      setIsRewardDialogOpen(false);
      resetRewardForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetRewardForm = () => {
    setRewardForm({
      name: '',
      description: '',
      points_required: '',
      image_url: '',
      stock: '0',
      start_date: '',
      end_date: '',
      birthday_only: false,
      active: true
    });
    setEditingReward(null);
  };

  const editReward = (reward) => {
    setEditingReward(reward);
    setRewardForm({
      name: reward.name,
      description: reward.description || '',
      points_required: reward.points_required.toString(),
      image_url: reward.image_url || '',
      stock: reward.stock.toString(),
      start_date: reward.start_date || '',
      end_date: reward.end_date || '',
      birthday_only: !!reward.birthday_only,
      active: reward.active
    });
    setIsRewardDialogOpen(true);
  };

  const deleteReward = async (id) => {
    if (!window.confirm('¿Eliminar esta recompensa?')) return;
    try {
      await axios.delete(`${API}/loyalty/rewards/${id}`, getAuthHeaders());
      toast.success('Recompensa eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const viewUserDetail = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(`${API}/loyalty/users/${user.id}`, getAuthHeaders());
      setUserDetail(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.whatsapp.includes(searchQuery) ||
    u.user_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-loyalty">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Programa de Lealtad</h1>
            <p className="text-zinc-500 mt-1">Gestiona puntos, recompensas y usuarios</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-gold" />
                <div>
                  <p className="text-2xl font-heading text-white">{stats.total_users}</p>
                  <p className="text-xs text-zinc-500">Usuarios</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-heading text-white">{stats.new_users_this_month}</p>
                  <p className="text-xs text-zinc-500">Nuevos este mes</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-heading text-white">{stats.total_checkins}</p>
                  <p className="text-xs text-zinc-500">Check-ins</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-heading text-white">{stats.total_redemptions}</p>
                  <p className="text-xs text-zinc-500">Canjes</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-heading text-white">{stats.total_points_circulation.toLocaleString()}</p>
                  <p className="text-xs text-zinc-500">Puntos en circulación</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger 
              value="config" 
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </TabsTrigger>
            <TabsTrigger 
              value="rewards"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Gift className="w-4 h-4 mr-2" />
              Recompensas ({rewards.length})
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Users className="w-4 h-4 mr-2" />
              CRM ({users.length})
            </TabsTrigger>
          </TabsList>

          {/* Config Tab */}
          <TabsContent value="config" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800 p-6 max-w-md">
              <h3 className="font-heading text-lg text-white mb-6">Configuración de Puntos</h3>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Puntos por Check-in</Label>
                  <Input
                    type="number"
                    value={config.points_per_checkin}
                    onChange={(e) => setConfig({ ...config, points_per_checkin: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500">Puntos que gana el usuario por cada visita</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Puntos por Referir</Label>
                  <Input
                    type="number"
                    value={config.points_per_referral}
                    onChange={(e) => setConfig({ ...config, points_per_referral: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500">Puntos para quien refiere a un nuevo usuario</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Bono para Nuevo Usuario Referido</Label>
                  <Input
                    type="number"
                    value={config.referral_bonus_for_new_user}
                    onChange={(e) => setConfig({ ...config, referral_bonus_for_new_user: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <p className="text-xs text-zinc-500">Puntos que recibe el nuevo usuario al registrarse con código</p>
                </div>
                <Button onClick={saveConfig} className="w-full bg-gold text-black hover:bg-gold-dark">
                  Guardar Configuración
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={isRewardDialogOpen} onOpenChange={(open) => { setIsRewardDialogOpen(open); if (!open) resetRewardForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-gold text-black hover:bg-gold-dark">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Recompensa
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-white">
                      {editingReward ? 'Editar Recompensa' : 'Nueva Recompensa'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRewardSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Nombre</Label>
                      <Input
                        value={rewardForm.name}
                        onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                        placeholder="Corte Gratis"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Descripción</Label>
                      <Input
                        value={rewardForm.description}
                        onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                        placeholder="Un corte de cabello gratis"
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Puntos Requeridos</Label>
                      <Input
                        type="number"
                        value={rewardForm.points_required}
                        onChange={(e) => setRewardForm({ ...rewardForm, points_required: e.target.value })}
                        placeholder="100"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">URL de Imagen</Label>
                      <Input
                        value={rewardForm.image_url}
                        onChange={(e) => setRewardForm({ ...rewardForm, image_url: e.target.value })}
                        placeholder="https://..."
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                      {rewardForm.image_url && (
                        <img src={rewardForm.image_url} alt="Preview" className="w-20 h-20 object-cover rounded mt-2" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Stock Disponible (0 = ilimitado)</Label>
                      <Input
                        type="number"
                        value={rewardForm.stock}
                        onChange={(e) => setRewardForm({ ...rewardForm, stock: e.target.value })}
                        placeholder="0"
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>

                    {/* Birthday-only checkbox */}
                    <label className="flex items-center gap-3 p-3 rounded-sm bg-pink-500/5 border border-pink-500/30 cursor-pointer hover:border-pink-500/50 transition-colors" data-testid="birthday-only-label">
                      <input
                        type="checkbox"
                        checked={!!rewardForm.birthday_only}
                        onChange={(e) => setRewardForm({ ...rewardForm, birthday_only: e.target.checked })}
                        className="w-4 h-4 rounded bg-zinc-900 border-zinc-600 text-pink-500 focus:ring-pink-500"
                        data-testid="reward-birthday-only-checkbox"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-white font-medium flex items-center gap-2">
                          🎂 Solo canjeable el día del cumpleaños
                        </span>
                        <p className="text-[10px] text-zinc-500">El usuario sólo podrá canjear esta recompensa si su día/mes de cumpleaños coincide con el día de hoy y tiene los puntos suficientes.</p>
                      </div>
                      {rewardForm.birthday_only && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-pink-500/20 text-pink-400 font-semibold uppercase tracking-wider">Cumpleaños</span>
                      )}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Fecha Inicio</Label>
                        <Input
                          type="date"
                          value={rewardForm.start_date}
                          onChange={(e) => setRewardForm({ ...rewardForm, start_date: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Fecha Fin</Label>
                        <Input
                          type="date"
                          value={rewardForm.end_date}
                          onChange={(e) => setRewardForm({ ...rewardForm, end_date: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-400">Activa</Label>
                      <Switch
                        checked={rewardForm.active}
                        onCheckedChange={(checked) => setRewardForm({ ...rewardForm, active: checked })}
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsRewardDialogOpen(false)}
                        className="flex-1 border-zinc-700"
                      >
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1 bg-gold text-black hover:bg-gold-dark">
                        {editingReward ? 'Actualizar' : 'Crear'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {rewards.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Gift className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay recompensas creadas</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rewards.map((reward) => (
                  <Card key={reward.id} className={`bg-zinc-900/50 border-zinc-800 overflow-hidden ${!reward.active && 'opacity-50'}`}>
                    {reward.image_url && (
                      <div className="h-32 bg-zinc-800">
                        <img src={reward.image_url} alt={reward.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-white">{reward.name}</h4>
                        {reward.birthday_only && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-pink-500/20 text-pink-400 font-semibold uppercase tracking-wider inline-flex items-center gap-1 mt-1" data-testid={`admin-birthday-badge-${reward.id}`}>
                            🎂 Solo cumpleaños
                          </span>
                        )}
                        <div className="flex gap-1">
                          <button onClick={() => editReward(reward)} className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button onClick={() => deleteReward(reward.id)} className="p-1.5 rounded bg-zinc-800 hover:bg-red-500/20 text-zinc-400 hover:text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {reward.description && (
                        <p className="text-sm text-zinc-500 mb-3">{reward.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-gold font-semibold">{reward.points_required} pts</span>
                        <span className="text-xs text-zinc-500">
                          {reward.stock > 0 ? `${reward.redeemed_count}/${reward.stock}` : 'Ilimitado'}
                        </span>
                      </div>
                      {(reward.start_date || reward.end_date) && (
                        <p className="text-xs text-zinc-600 mt-2">
                          {reward.start_date && `Desde ${reward.start_date}`}
                          {reward.start_date && reward.end_date && ' - '}
                          {reward.end_date && `Hasta ${reward.end_date}`}
                        </p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users/CRM Tab */}
          <TabsContent value="users" className="mt-6">
            {/* Search and Filter */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Buscar por nombre, WhatsApp o código..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="w-full sm:w-64">
                <Select value={selectedBranchFilter} onValueChange={setSelectedBranchFilter}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="crm-branch-filter">
                    <MapPin className="w-4 h-4 mr-2 text-zinc-500" />
                    <SelectValue placeholder="Filtrar por sucursal" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id} className="text-white">
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay usuarios registrados</p>
              </Card>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-800/50">
                      <tr>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">CÓDIGO</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">NOMBRE</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">WHATSAPP</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">SUCURSAL</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">CUMPLEAÑOS</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">PUNTOS</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">VISITAS</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">REFERIDOS</th>
                        <th className="text-left p-4 text-xs text-zinc-500 font-medium">REGISTRO</th>
                        <th className="p-4"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-zinc-800/30">
                          <td className="p-4">
                            <span className="font-mono text-gold bg-gold/10 px-2 py-1 rounded text-sm">
                              {user.user_code}
                            </span>
                          </td>
                          <td className="p-4 text-white">{user.name}</td>
                          <td className="p-4 text-zinc-400">{user.whatsapp}</td>
                          <td className="p-4">
                            {user.branch_name ? (
                              <span className="text-sm text-blue-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {user.branch_name}
                              </span>
                            ) : (
                              <span className="text-zinc-600 text-sm">-</span>
                            )}
                          </td>
                          <td className="p-4 text-zinc-400">
                            {user.birthday_day}/{user.birthday_month}
                          </td>
                          <td className="p-4">
                            <span className="text-gold font-semibold">{user.total_points}</span>
                          </td>
                          <td className="p-4 text-zinc-400">{user.checkin_count}</td>
                          <td className="p-4 text-zinc-400">{user.referral_count}</td>
                          <td className="p-4 text-zinc-500 text-sm">
                            {new Date(user.created_at).toLocaleDateString('es-MX')}
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => viewUserDetail(user)}
                              className="p-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-400"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* User Detail Dialog */}
        <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserDetail(null); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white">
                Detalle de Usuario
              </DialogTitle>
            </DialogHeader>
            {userDetail && (
              <div className="space-y-6 mt-4">
                {/* User Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-800 rounded-sm">
                    <p className="text-xs text-zinc-500">Código</p>
                    <p className="text-xl font-mono text-gold">{userDetail.user.user_code}</p>
                  </div>
                  <div className="p-4 bg-zinc-800 rounded-sm">
                    <p className="text-xs text-zinc-500">Puntos</p>
                    <p className="text-xl text-gold font-semibold">{userDetail.user.total_points}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500">Nombre</p>
                    <p className="text-white">{userDetail.user.name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">WhatsApp</p>
                    <p className="text-white">{userDetail.user.whatsapp}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Cumpleaños</p>
                    <p className="text-white">{userDetail.user.birthday_day}/{userDetail.user.birthday_month}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Registrado</p>
                    <p className="text-white">{new Date(userDetail.user.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                </div>

                {/* Transactions */}
                <div>
                  <h4 className="text-sm text-zinc-500 uppercase tracking-wider mb-3">Historial de Puntos</h4>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {userDetail.transactions.map((t) => (
                        <div key={t.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                          <div>
                            <p className="text-sm text-white">{t.description}</p>
                            <p className="text-xs text-zinc-500">{new Date(t.created_at).toLocaleString('es-MX')}</p>
                          </div>
                          <span className={`font-semibold ${t.points > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {t.points > 0 ? '+' : ''}{t.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {/* Redemptions */}
                {userDetail.redemptions.length > 0 && (
                  <div>
                    <h4 className="text-sm text-zinc-500 uppercase tracking-wider mb-3">Canjes Realizados</h4>
                    <div className="space-y-2">
                      {userDetail.redemptions.map((r) => (
                        <div key={r.id} className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-sm">
                          <div>
                            <p className="text-sm text-white">{r.reward_name}</p>
                            <p className="text-xs text-zinc-500">Código: {r.redemption_code}</p>
                          </div>
                          <span className="text-purple-400 font-semibold">-{r.points_used} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
